// C8b (her order, 2026-08-12, mid-run) — **A PHONE NUMBER ON THE SIGN-UP PAGE, AND THE ONE LAW THAT
// SAYS WHAT A DIALABLE NUMBER IS.**
//
// It sits directly before C9 because it is what FEEDS it: the SMS rail's reachable count is exactly
// the members who gave one, and today that number is whatever is already on file — which, measured
// against the 116 migrations, is NOTHING. There is no `phone` column on `users` anywhere in this
// repo, no field on the front door, no normaliser and no consent row. The only phone in the schema is
// `funnel_leads.phone` (migration 101), which is a STRANGER'S optional number on a lead row: a
// different table and a different person.
//
// >>> **AND THAT LEAD ROW IS EXACTLY WHY THIS LAW IS SHARED RATHER THAN WRITTEN BESIDE IT.** <<<
// `src/funnelCapture.js` already carries a phone-shape rule with its own R18 reasoning written out.
// A member normaliser written next to it is **two answers to *what is a dialable number*** (R28/R29),
// and the copy that drifts is the one deciding whether her SMS audience can be reached at all. So the
// capture's rule DELEGATES here, and this module **imports NOTHING**, which is the entry requirement
// that lets a `.mjs` gate load it and RUN it.
//
// >>> **E.164 IS A THIRD FACT, NOT A SECOND SPELLING OF THE OTHER TWO (R18).** <<< *What she typed*,
// *is it dialable*, and *the `+1` form we store* are three different things, and the funnel's rule
// answers only the middle one. **What she typed is stored NOWHERE** — P2-4C binds on anything durable
// and a raw string on a permanent row is a member-typed value with no reader. **And a number this
// cannot normalise is REFUSED at the field rather than stored raw**, because an un-dialable row in an
// SMS audience is a send that fails per recipient inside a loop, at a cost, with nothing on any
// screen to say why.

// ---------------------------------------------------------------------------
// WHAT COUNTS AS A NUMBER.
//
// **THE DEFAULT COUNTRY CODE IS `1` AND IT IS DECLARED RATHER THAN ASSUMED.** This house's members
// are overwhelmingly North American and a bare ten-digit number is what almost every one of them will
// type; a member outside that plan types her own `+`, which is the one form that is unambiguous
// everywhere. **A number that is neither is REFUSED and never guessed at** — guessing prefixes a
// country code onto somebody else's number, which is a message delivered to a stranger.
export const DEFAULT_COUNTRY_CODE = "1";

// E.164's own ceiling is fifteen digits INCLUDING the country code, and its floor is a real one: the
// shortest national numbers in service are seven digits behind a country code. Held at BOTH ends,
// because a floor alone accepts a forty-digit paste and a ceiling alone accepts `+1`.
export const PHONE_MIN_DIGITS = 8;
export const PHONE_MAX_DIGITS = 15;

/** The reasons a number is refused. Named, because *this is not a number* and *this house cannot tell
 *  which country* send her to two different corrections (R18). */
export const PHONE_REFUSALS = {
  empty: "",
  "not-a-number": "That does not look like a phone number.",
  "too-short": "That phone number is too short. Include the area code.",
  "too-long": "That phone number is too long.",
  "unknown-country": "Start with + and your country code so we know where to reach you.",
};

/**
 * **THE ONE NORMALISER.** Three outcomes, never two (R18):
 *   `{ ok: true,  e164 }`         — a number this house can dial and store.
 *   `{ ok: false, why: "empty" }` — nothing was typed, which is NOT an error: the field is optional.
 *   `{ ok: false, why }`          — something was typed and it cannot be dialled.
 *
 * The middle one is the load-bearing outcome. Collapsing it into the third refuses every member who
 * leaves the field alone, on the front door, which is a conversion change nobody ordered.
 */
export function normalizePhone(input) {
  const raw = String(input ?? "").trim();
  if (!raw) return { ok: false, why: "empty", e164: "" };

  // A leading `+` is the member telling us the country code herself, and it is the ONLY thing in the
  // string that carries meaning beyond the digits. Read BEFORE the strip, because the strip removes
  // it — and everything a keypad, a paste from a contact card or a form autofill can add (spaces,
  // dashes, brackets, dots, a leading `00`) is punctuation.
  const plus = raw.startsWith("+");
  let digits = raw.replace(/\D/g, "");
  if (!digits) return { ok: false, why: "not-a-number", e164: "" };

  // `00` is the international prefix half the world dials instead of `+`, and reading it as part of
  // the number turns a perfectly good international number into an un-dialable one.
  const international = plus || (!plus && digits.startsWith("00"));
  if (!plus && digits.startsWith("00")) digits = digits.slice(2);

  // **A TEN-DIGIT NORTH AMERICAN NUMBER GETS THE DEFAULT CODE AND NOTHING ELSE DOES.** Eleven digits
  // beginning `1` is the same number with the code already on it. Anything else without a `+` is a
  // number this house cannot place, and it is REFUSED rather than prefixed — R19's direction, because
  // a wrong guess here is a message sent to somebody who is not her.
  if (!international) {
    if (digits.length === 10) digits = `${DEFAULT_COUNTRY_CODE}${digits}`;
    else if (digits.length === 11 && digits.startsWith(DEFAULT_COUNTRY_CODE)) { /* already carries it */ }
    else if (digits.length < PHONE_MIN_DIGITS) return { ok: false, why: "too-short", e164: "" };
    else return { ok: false, why: "unknown-country", e164: "" };
  }

  if (digits.length < PHONE_MIN_DIGITS) return { ok: false, why: "too-short", e164: "" };
  if (digits.length > PHONE_MAX_DIGITS) return { ok: false, why: "too-long", e164: "" };
  // A country code cannot begin with 0, so a leading zero after a `+` is a national trunk prefix
  // somebody left on — which dials nowhere.
  if (digits.startsWith("0")) return { ok: false, why: "unknown-country", e164: "" };
  return { ok: true, why: "", e164: `+${digits}` };
}

/** The sentence for a refused number, or "" when there is nothing to correct. */
/**
 * C9 — **THE DIALABLE FORM, OR NOTHING.** A thin reader over `normalizePhone` for the callers that
 * only ever want *may this house ring it, and in what form* — the Twilio transport and the broadcast
 * loop's third branch.
 *
 * **IT IS A READER AND NEVER A SECOND NORMALISER (C8b's own ruling, R28/R29).** That round exists
 * because a phone-shape rule written beside another one is two answers to *what is a dialable
 * number*, with the copy that drifts deciding whether her SMS audience can be reached at all. This
 * adds no rule: it asks the one law and returns the E.164 string or `""`.
 */
export function verifiedPhone(input) {
  const v = normalizePhone(input);
  return v.ok === true ? v.e164 : "";
}

export function phoneRefusalText(why) {
  return Object.prototype.hasOwnProperty.call(PHONE_REFUSALS, String(why || "")) ? PHONE_REFUSALS[String(why || "")] : "";
}

// ---------------------------------------------------------------------------
// C8b(b) — **THE SMS OPT-IN IS ITS OWN EXPLICIT, UNCHECKED CONSENT AND IS NEVER IMPLIED BY THE
// FIELD.** C9 carries STOP-to-opt-out compliance; the other half of that law is that marketing SMS
// needs an opt-in the member actually gave. **A number typed for account recovery is not permission
// to broadcast to it**, and collapsing those two is the failure that costs a sending NUMBER rather
// than a screen.
//
// It rides `termsConsent.js`'s own shape — a VERSION and a TIMESTAMP written onto her row — so *what
// she agreed to* and *when* are answerable per member rather than inferred from the fact that a
// number is on file. It is a SEPARATE version from the Terms, because they are separate agreements
// and re-wording one may not silently re-date the other (R18).
export const SMS_CONSENT_VERSION = "2026-08-15";

// **THE SENTENCE IS DATA AND IT IS ONE DECLARATION**, so the three doors P3 will add cannot promise
// three different things. It names WHO sends, WHAT kind of message, that rates apply and how to
// stop — which is the wording an SMS carrier's own compliance review asks for, and the wording C9's
// STOP handler is the other half of.
export const SMS_CONSENT_TEXT =
  "Yes, text me. I agree to receive marketing text messages from 12th House at this number. "
  + "Message and data rates may apply. Reply STOP at any time to stop.";

// **WHICH DOOR THE NUMBER CAME THROUGH, DECLARED.** Without it a later STOP audit cannot say what she
// agreed to or where — and P3's *one shared save path* clause means the other two doors are CALLERS
// on this roster rather than a second writer.
export const PHONE_DOORS = ["signup", "popup", "menu"];
export const phoneDoorValid = (door) => PHONE_DOORS.includes(String(door || ""));

/**
 * **THE ONE SAVE SHAPE, SO THE THREE DOORS CANNOT WRITE THREE DIFFERENT ROWS.**
 *
 * Four outcomes, and the first two are both NOT errors (R18):
 *   `skip`    — nothing was typed. The field is optional; there is nothing to write and nothing to say.
 *   `save`    — a dialable number, with her consent decision beside it.
 *   `refuse`  — something was typed that cannot be dialled, with the sentence to show her.
 *   `refuse`  — the door is not one this house knows, which fails CLOSED rather than filing a row
 *               nobody can account for.
 *
 * **THE CONSENT IS `=== true` AND NEVER A TRUTHY READ** (R53/R136/R137): it arrives off JSON, where
 * `Boolean("false")` is `true`, and on this field a coercion is a member enrolled in marketing SMS
 * she never agreed to — the one mistake here that costs a sending number.
 */
export function phoneSubmission({ phone, smsConsent, door } = {}) {
  const parsed = normalizePhone(phone);
  if (!parsed.ok && parsed.why === "empty") return { action: "skip", why: "empty", message: "" };
  if (!parsed.ok) return { action: "refuse", why: parsed.why, message: phoneRefusalText(parsed.why) };
  if (!phoneDoorValid(door)) return { action: "refuse", why: "unknown-door", message: "" };
  const agreed = smsConsent === true;
  return {
    action: "save",
    why: "",
    message: "",
    e164: parsed.e164,
    door: String(door),
    // **A REFUSED OPT-IN IS RECORDED AS A REFUSED OPT-IN, NEVER AS AN ABSENT ONE.** The version and
    // the moment are written only when she said yes, so a later audit reads a version she agreed to
    // rather than a flag with no document behind it — and a member who left the box unticked is a
    // number this house may use to reach her about her own account and may not broadcast to.
    smsConsent: agreed,
    smsConsentVersion: agreed ? SMS_CONSENT_VERSION : "",
  };
}

// =================================================================================================
// P2 — **THE MEMBER PHONE POPUP: ONE ASK, AND EITHER ANSWER ENDS IT FOREVER.**
//
// Her order: *"Existing members with no phone on file see ONE warm popup ... >>> THE ONCE LAW:
// saving OR declining records the answer TO THE DATABASE and the popup never shows again. No
// nagging ever. <<< Never during a generation or mid-conversation — only on a clean room landing."*
//
// **>>> THE DIRECTION IS THE RULING, AND IT IS THE OPPOSITE OF THIS HOUSE'S USUAL ONE (R19/R8-4).
// <<<** Almost every law here fails toward showing a member something, because a missing control is
// silent and an extra one is reportable. **Not this one.** Her clause forbids nagging BY NAME, and
// the cost of the two directions is not symmetric: asking wrongly puts a popup in front of a member
// who has already said no — twice, three times, on a screen she opens every morning — and there is
// nothing she can do about it. Asking too little costs this house one phone number. **So every
// uncertainty answers `no`:** an unread session, an unread row, an unreadable stamp, a page this
// law does not recognise. `p2-mutations.mjs` M4 is the fail-open version, and it reads as a
// perfectly ordinary default.
//
// **AND `hold` IS A THIRD STATE RATHER THAN A KINDER `no` (R18), BECAUSE THE TWO HAVE DIFFERENT
// FUTURES.** *We have not looked yet* becomes an ask the moment her row lands; *she has answered*
// never does. Collapsing them is L5.1's own reported defect one surface over — a paying Apprentice
// landing on her own bookmark and getting the membership box thrown in her face for a beat, because
// `isMember` is false until the session answers. A popup that flashes for one frame on every load
// is nagging that nobody can even report properly.
// =================================================================================================

// **HER COPY, AND THE REASON COMES BEFORE THE ASK — WHICH IS HER OWN CLAUSE AND NOT A STYLE NOTE.**
// A member who is shown a phone field first is being asked for something; a member who is told what
// it buys her first is being offered something. The lede is the offer, in her family of words, and
// the field is underneath it.
export const PHONE_PROMPT_COPY = {
  title: "Want the house to reach you where you are?",
  lede: "Add your number and get your daily line and time-sensitive guidance by text, "
      + "even without the app.",
  save: "Save my number",
  decline: "Not now",
};

// **THE PAGE THIS MAY OPEN ON, DECLARED RATHER THAN IMPLIED.** Her clause is *only on a clean room
// landing*, and a roster is what makes that a fact a gate can run rather than a sentence somebody
// remembers. A page this law does not recognise is NOT a room landing — the fail-closed direction
// above — so a room added next year does not silently inherit a popup nobody decided to put there.
export const PHONE_PROMPT_PAGES = ["my-room"];
export const phonePromptPage = (page) => PHONE_PROMPT_PAGES.includes(String(page || ""));

/**
 * **MAY THE HOUSE ASK HER FOR A NUMBER RIGHT NOW?**
 *
 * `shouldOfferPush`'s own shape one ask over (P3-2B): a pure law answering with REASONS rather than
 * a bare false, so the refusals are REPORTED and a popup that never appears has an answer instead of
 * a silence. Every input DEFAULTS to the value that refuses, so a caller that forgets one asks
 * nobody — which is the only default that cannot cost a member her morning.
 *
 * Three verdicts, never two:
 *   `hold`   — her row has not come back yet. Ask again when it has; draw nothing now.
 *   `no`     — settled, with the reasons. This never becomes an ask.
 *   `ask`    — a signed-in member with a room, no number, no answer on file, standing still on a
 *              clean room landing.
 */
export function phonePromptState({
  known = false,
  signedIn = false,
  hasRoom = false,
  hasPhone = true,
  answeredAt = null,
  page = "",
  // **HER CLAUSE 5 IS TWO FACTS AND THEY ARE ASKED AS TWO (R18).** *A reading is being made* and
  // *a conversation is open* are different interruptions: the first is work she paid for and is
  // watching, the second is a thread she is in the middle of. A law with one flag reads perfectly
  // and lets the popup land on whichever one the caller forgot.
  generating = true,
  conversationOpen = true,
} = {}) {
  // **THE UNREAD ROW OUTRANKS EVERYTHING AND IS ASKED FIRST**, because every clause below it is a
  // claim about a row nobody has read. Asked second, a member with no row yet reads as a member
  // with no phone — which is the one wrong way to get an `ask`.
  if (known !== true) return { verdict: "hold", reasons: ["her row has not come back yet"] };

  const reasons = [];
  if (!signedIn) reasons.push("not signed in");
  if (!hasRoom) reasons.push("no room yet");
  // **A NUMBER ON FILE ENDS THE QUESTION WITHOUT NEEDING AN ANSWER STAMP.** She gave one at signup;
  // asking for it again is the nagging her clause names, and it is the commonest way this popup
  // could reach the wrong member.
  if (hasPhone === true) reasons.push("she already gave us a number");
  // **THE ONCE LAW.** Any stamp at all closes it — that is her *saving OR declining*, one record for
  // two answers, because the question is *has she answered* and not *what did she say*. The stamp is
  // asked for PRESENCE and never coerced: `Number(true)` is 1 and an epoch of 0 is a real date in
  // 1970, so a truthy read here would either re-ask her forever or close a question nobody put
  // (R53/R136/R137).
  if (answeredAt !== null && answeredAt !== undefined && answeredAt !== "") reasons.push("she has already answered");
  if (!phonePromptPage(page)) reasons.push("not a room landing");
  // Her clause 5, both halves.
  if (generating !== false) reasons.push("a reading is being made");
  if (conversationOpen !== false) reasons.push("a conversation is open");

  return reasons.length ? { verdict: "no", reasons } : { verdict: "ask", reasons: [] };
}

export const phonePromptOffered = (state) => (state?.verdict === "ask");

/**
 * **WHAT THE POPUP'S ANSWER IS, AND WHY DECLINING IS ITS OWN OUTCOME RATHER THAN AN EMPTY SAVE.**
 *
 * `phoneSubmission` already answers `skip` for an empty field, because at SIGNUP an empty field is a
 * member who simply did not fill one in and there is nothing to record. **Here it is the opposite:
 * an empty field with the Not-now button pressed IS her answer, and it is the one that has to reach
 * the database** — her clause says so in as many words. Reusing `skip` would silently write nothing
 * and she would meet the popup again tomorrow, which is the exact failure her *no nagging ever*
 * forbids. **M8 is that reuse and it looks like a tidy consolidation.**
 *
 * Four outcomes:
 *   `decline` — record the answer, write no number, keep every consent column exactly as it was.
 *   `save`    — `phoneSubmission`'s own answer, with the stamp riding beside it.
 *   `refuse`  — a number that cannot be dialled, with her sentence. **The stamp is NOT written**:
 *               she tried to answer and the house could not take it, so the question is still open
 *               and closing it would lose her the number she was trying to give (R18).
 *   `refuse`  — an answer this law does not know, which fails closed.
 */
export const PHONE_PROMPT_ANSWERS = ["save", "decline"];

export function phonePromptAnswer({ answer, phone, smsConsent } = {}) {
  const a = String(answer || "");
  if (a === "decline") return { action: "decline", answered: true, message: "" };
  if (a !== "save") return { action: "refuse", answered: false, why: "unknown-answer", message: "" };
  // **THE DOOR IS THIS LAW'S AND NEVER THE CALLER'S.** P3's *one shared save path* clause means the
  // three doors are callers on one writer, and a door a client can type is a door a client can
  // CHOOSE — which would put a menu save on her signup audit and a popup save on neither.
  const sub = phoneSubmission({ phone, smsConsent, door: "popup" });
  if (sub.action === "save") return { ...sub, answered: true };
  // An empty field with SAVE pressed is not an answer: she pressed the wrong button, and recording
  // it would close the question she was about to answer.
  if (sub.action === "skip") return { action: "refuse", answered: false, why: "empty", message: phoneRefusalText("empty") };
  return { ...sub, answered: false };
}

// =================================================================================================
// P3 — **THE STANDING DOOR. DECLINING IS NEVER FINAL.**
//
// Her amendment: *"The Member menu gains SMS Notifications. Any member, any time, taps it, types a
// number, gets the same consent line, presses accept, and it saves as a consented marketing number,
// joining the SMS audience instantly. A member who already has one sees it with UPDATE or REMOVE,
// and remove honours instantly, the same as STOP. ONE SHARED SAVE PATH with the popup and the signup
// field — one truth in the database."*
//
// **>>> THIS IS THE THIRD CALLER AND NEVER THE SECOND WRITER, WHICH IS HER OWN CLAUSE READ EXACTLY.
// <<<** `phoneSubmission` is still the one law, `savePhone` is still the one writer, and `menu` was
// already on `PHONE_DOORS` before this round existed. What P3 adds is a STATE (what she is looking
// at) and a REMOVAL (an act the other two doors do not have), and nothing else.
//
// **>>> AND THE REMOVAL IS THE ROUND, BECAUSE IT IS THE ONE ACT `savePhone` REFUSES TO PERFORM. <<<**
// That writer's own comment says the consent columns *"are NEVER cleared here… withdrawal is C9's
// STOP, and it is a different act with a different record (R18)"*. So a remove written as an empty
// save would either do nothing or quietly become a second writer. It is its own store call, and C9's
// STOP handler is a CALLER on it — the pre-measure's own instruction, so the rail lands with one
// answer to *what taking a number off file means* rather than two.
// =================================================================================================

/**
 * **WHAT SHE IS LOOKING AT, AND `unknown` IS FIRST FOR `pushPanelState`'s OWN REASON (P4-1).**
 *
 * *This member has no number* and *we have not read her row yet* are different facts, and a panel
 * that draws the empty state while it is still reading tells a member who gave her number at signup
 * that this house does not have it — and invites her to type it again. It is W5-2/R39's third state
 * on a control rather than on a list, and it is the same `hold` the popup's own law opens with.
 *
 * **AND `number-only` IS A FOURTH STATE RATHER THAN A KINDER `on`, BECAUSE IT IS REACHABLE TODAY.**
 * C8b's signup field carries an UNCHECKED opt-in that never blocks the button, and P2's popup does
 * the same — so a member with a number and no marketing consent is an ordinary outcome of two doors
 * that already ship. She is reachable about her own account and is in NO audience, and a panel
 * reporting her as `on` would tell her she is getting texts that are not coming. **The face says
 * what IS** (ITEM 7's law, which `PushSettingsPanel` is built on one preference over).
 */
export const SMS_PANEL_STATES = {
  UNKNOWN: "unknown",
  NONE: "none",
  NUMBER_ONLY: "number-only",
  ON: "on",
};
export const SMS_PANEL_STATE_VALUES = Object.values(SMS_PANEL_STATES);

export function smsPanelState(facts) {
  if (!facts || typeof facts !== "object") return SMS_PANEL_STATES.UNKNOWN;
  // **PRESENCE, NEVER TRUTHINESS, ON ALL THREE (R53/R136/R137).** Every one of these arrives off
  // JSON, where `Boolean("false")` is `true` — and on the consent flag a coercion is a member told
  // she is enrolled in marketing texts she never agreed to, which is the one mistake on this panel
  // that costs a sending number rather than a screen.
  if (facts.known !== true) return SMS_PANEL_STATES.UNKNOWN;
  if (facts.hasPhone !== true) return SMS_PANEL_STATES.NONE;
  return facts.smsConsent === true ? SMS_PANEL_STATES.ON : SMS_PANEL_STATES.NUMBER_ONLY;
}

/**
 * **WHICH CONTROLS EXIST, DERIVED FROM THE STATE RATHER THAN DECIDED IN THE MARKUP** (R31, and
 * `pushPanelAction`'s own shape): a panel that decides its own controls is a panel no gate can run.
 *
 * Two facts, never one (R18). **The FIELD is her clause 1** — *any member, any time* — so it exists
 * wherever her row has answered, and typing into it is an add for a member with nothing and an
 * update for a member with something; one control, because *add* and *update* are the same write and
 * two buttons for one write is how the two come to disagree. **REMOVE is her clause 3** and exists
 * only where there is something to remove: a Remove drawn over an empty row is A2's dead box, and a
 * tap on it would answer honestly that it removed nothing while looking like a failure.
 *
 * **`unknown` DRAWS NEITHER**, which is the state this panel spends its first moment in.
 */
export function smsPanelControls(state) {
  const known = SMS_PANEL_STATE_VALUES.includes(state) && state !== SMS_PANEL_STATES.UNKNOWN;
  return {
    field: known,
    remove: known && state !== SMS_PANEL_STATES.NONE,
  };
}

// **AN EMPTY FIELD WITH SAVE PRESSED IS AN ERROR HERE AND IS NOT ONE AT SIGNUP, AND THE SENTENCE IS
// WHY THE ROSTER WAS NOT WIDENED.** `PHONE_REFUSALS.empty` is `""` on purpose: at the front door an
// empty field is a member who did not fill one in, and there is nothing to correct. On this panel
// she pressed a button that does one thing, so she is told what to do — **and it names the other
// control**, because a member who meant to clear her number is one tap away from the thing that
// really does it, and sending her back to an empty field to press Save again is a dead end.
export const SMS_MENU_EMPTY_REFUSAL =
  "Type a number first, or use Remove to take yours off file.";

/**
 * **WHAT A TAP ON THIS PANEL IS, AND THE DOOR IS THIS LAW'S CONSTANT (R41).**
 *
 * `phonePromptAnswer`'s own shape one door over, and the reason is identical: a door a client can
 * type is a door a client can CHOOSE, which would file a menu save on her signup audit — and the
 * whole point of `phone_source` is that a later STOP audit can say WHERE she agreed.
 *
 * Four outcomes:
 *   `remove`  — take the number and the consent off her row. Her clause 3.
 *   `save`    — `phoneSubmission`'s own answer, filed under `menu`.
 *   `refuse`  — a number that cannot be dialled, with the sentence to show her.
 *   `refuse`  — an empty field, or an action this law does not know, which fails CLOSED.
 */
// **THE WORDS, AS DATA, ONE ROW PER STATE** — `PUSH_PANEL_KEYS`' own shape one preference over, so a
// state added to the roster tomorrow either carries its own copy or the gate names it (R28/R29's
// biconditional, rather than a `switch` whose default is silence).
//
// **THE SAVE BUTTON'S WORD IS DERIVED FROM THE STATE AND IS HER CLAUSE 3** — *a member who already
// has one sees UPDATE* — while the FIELD underneath it is one control, because adding and updating
// are the same write and two buttons for one write is how the two come to disagree. `save: null` on
// `unknown` is a DECLARED absence (R18) rather than a missing key: it says this state has been
// looked at and has no control, which is a different fact from a row somebody forgot to write.
export const SMS_PANEL_KEYS = {
  [SMS_PANEL_STATES.UNKNOWN]: { state: "sms.checking", save: null },
  [SMS_PANEL_STATES.NONE]: { state: "sms.none", save: "sms.save" },
  [SMS_PANEL_STATES.NUMBER_ONLY]: { state: "sms.numberOnly", save: "sms.update" },
  [SMS_PANEL_STATES.ON]: { state: "sms.on", save: "sms.update" },
};

// A state nobody rostered would draw a blank sentence over a blank button, which is W12-5's
// DATE-OF-BIRTH-over-nothing one control down. It answers the row of a state that is definitionally
// present instead, and the gate holds the roster TOTAL so this is never reached in production.
export function smsPanelCopy(state) {
  return SMS_PANEL_KEYS[state] || SMS_PANEL_KEYS[SMS_PANEL_STATES.UNKNOWN];
}

export const SMS_MENU_ACTIONS = ["save", "remove"];
export const smsMenuActionValid = (action) => SMS_MENU_ACTIONS.includes(String(action || ""));

export function smsMenuAnswer({ action, phone, smsConsent } = {}) {
  const a = String(action || "");
  if (a === "remove") return { action: "remove", why: "", message: "" };
  if (a !== "save") return { action: "refuse", why: "unknown-action", message: "" };
  const sub = phoneSubmission({ phone, smsConsent, door: "menu" });
  // **THE EMPTY CASE IS PROMOTED FROM `skip` TO `refuse` AND THAT IS THE ONE DIVERGENCE FROM THE
  // SIGNUP FIELD.** Left as `skip` the route would answer `ok: true` having written nothing, and she
  // would close the menu believing her number was saved — a silent success, which is the shape this
  // house refuses everywhere it can see one.
  if (sub.action === "skip") return { action: "refuse", why: "empty", message: SMS_MENU_EMPTY_REFUSAL };
  return sub;
}
